import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fragment, forwardRef, useEffect, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Select, MenuItem, Skeleton } from '@mui/material';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import TableRatesRow from './TableRatesRow';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';

const id = 'hsbc_smart_care_optimum_enhanced';

const plans = [
  { plan: 'bronze', label: 'Bronze' },
  { plan: 'silver', label: 'Silver' },
  { plan: 'gold', label: 'Gold' },
  { plan: 'platinum', label: 'Platinum' },
];

// Bronze has no outpatient cover
const outpatientPlans = ['silver', 'gold', 'platinum'];

// Response key prefix => row label. Selected through the '<id>_<key>' plan checkbox.
const outpatientModules = [
  { key: 'op_general_practitioner', label: 'Outpatient General Practitioner' },
  { key: 'op_specialist', label: 'Outpatient Specialist' },
];

// Response key => label. Dental isn't split by tier.
const dentalPlans = [
  { key: 'dental_plan_1', label: 'Plan 1' },
  { key: 'dental_plan_2', label: 'Plan 2' },
];

const HSBC_SmartCareOptimumEnhanced_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  loadings, setLoadings, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {

  const [dentalPlan, setDentalPlan] = useState(dentalPlans[0].key);

  // Only SGD and Annually are offered (the backend enforces the same)
  const currencies = ['SGD'];
  const paymentFrequencies = ['Annually'];

  const deductibleOptions = appConfig?.content?.deductibles?.[id]?.[specificCurrency[id]] || [];

  useEffect(() => {
    computedRates[id] = getComputedRatesForPlanComparison();
    setComputedRates(computedRates);
  }, [response, discounts, loadings, selectedPlans, dentalPlan]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'deductible', deductibles[id]);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
      addSelectedOption(id, 'dental_plan', dentalPlan);
    }, 1000);
  }, [specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], deductibles[id], discounts[id], loadings[id], dentalPlan]);

  useEffect(() => {
    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });

    setViewSelectedOption(id, 'deductible', (option_value) => {
      deductibles[id] = option_value;
      setDeductibles(deductibles);
    });

    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      // Quotations saved before the default was set stored an empty area
      specificAreaOfCoverage[id] = option_value || 'Singapore';
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'dental_plan', (option_value) => {
      setDentalPlan(option_value);
    });
  }, [selectedOptions]);

  function isModuleSelected(module) {
    return selectedPlans.includes(id + '_' + module);
  }

  function hasOutpatient(plan) {
    return outpatientPlans.includes(plan);
  }

  function getDentalPlanLabel() {
    return dentalPlans.find(d => d.key === dentalPlan)?.label || '';
  }

  // Flattens the response into '<plan>_<benefit>' fields for TableRatesRow
  function computeHSBCRates(rates) {
    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const obj = {
        'client': rates[i].client,
        'currency': rates[i].currency,
      };

      plans.forEach(({ plan }) => {
        obj[plan + '_base'] = rates[i][plan];

        outpatientModules.forEach(({ key }) => {
          obj[plan + '_' + key] = hasOutpatient(plan) ? rates[i][key + '_' + plan] : 'Unavailable';
        });

        obj[plan + '_dental'] = rates[i][dentalPlan];
      });

      computedRates.push(obj);
    }

    return computedRates;
  }

  function getComputedRates() {
    if (!response) return;

    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;
    const totalPercent = ((100 - discount + loading) * 0.01);

    return computeHSBCRates(response.rates).map(rates => {
      const obj = { ...rates };

      for (const [key, value] of Object.entries(rates)) {
        if (key !== 'client' && key !== 'currency' && typeof value === 'number') {
          obj[key] = value * totalPercent;
        }
      }

      return obj;
    });
  }

  function getComputedRatesForPlanComparison() {
    if (!response) return;

    const rates = response.rates;

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const obj = {
        'client': rates[i].client,
        'currency': rates[i].currency,
      };

      plans.forEach(({ plan }) => {
        obj[plan] = computeTotalPremiumPerClient(i, plan);
      });

      computedRates.push(obj);
    }

    return computedRates;
  }

  function computeTotalPremium(plan) {
    let total = 0;

    for (let i = 0; i < clients.length; i++) {
      const totalPerClient = computeTotalPremiumPerClient(i, plan);
      if (!isNaN(totalPerClient)) {
        total += totalPerClient;
      }
    }

    return total;
  }

  function computeTotalPremiumPerClient(clientIndex, plan) {
    const computedRates = getComputedRates();

    if (!computedRates) return 0;

    const rates = computedRates[clientIndex];

    if (!rates) return 0;

    if (!selectedPlans.includes(id + '_' + plan)) return 0;

    let total = 0;

    if (!isNaN(rates[plan + '_base'])) {
      total += rates[plan + '_base'];
    }

    if (hasOutpatient(plan)) {
      outpatientModules.forEach(({ key }) => {
        if (isModuleSelected(key) && !isNaN(rates[plan + '_' + key])) {
          total += rates[plan + '_' + key];
        }
      });
    }

    if (isModuleSelected('dental') && !isNaN(rates[plan + '_dental'])) {
      total += rates[plan + '_dental'];
    }

    return total;
  }

  function getBasePremiumPerClient(clientIndex, plan) {
    const computedRates = getComputedRates();

    return computedRates?.[clientIndex]?.[plan + '_base'];
  }

  function computeBasePremium(plan) {
    let total = 0;

    for (let i = 0; i < clients.length; i++) {
      const basePremium = getBasePremiumPerClient(i, plan);

      if (isNaN(basePremium) || typeof basePremium !== 'number') {
        return 'Unavailable';
      }

      total += basePremium;
    }

    return total;
  }

  function formatRate(rate) {
    if (isNaN(rate) || rate == 'Unavailable' || typeof rate === 'string') {
      return <span style={{ color: '#f44336' }}>{'Unavailable'}</span>
    }

    return getCurrencySign() + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function getCurrencySign() {
    const currency = getCurrency();

    if (currency === 'SGD') {
      return 'S$';
    }

    return '';
  }

  function getCurrency() {
    const computedRates = getComputedRates();

    if (!computedRates) return '';

    if (computedRates.length === 0) { return '' }

    return computedRates[0].currency;
  }

  function handleDeductibleChange(e) {
    const _deductibles = JSON.parse(JSON.stringify(deductibles));

    _deductibles[id] = e.target.value;

    setDeductibles(_deductibles);

    addToPendingTasks([id]);
  }

  const rates = response ? computeHSBCRates(response.rates) : [];

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2 }} variant='outlined' >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#6d0015', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#6d0015',
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 16,
            lineHeight: 1.2,
            height: 60,
            display: 'flex',
            alignItems: 'center'
          }}>SmartCare Optimum Enhanced</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: '#f8f9fa', px: 1.5, py: 0.5 }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/hsbc-life-logo.svg`}
              alt="HSBC Life"
              style={{ height: 55, userSelect: 'none', WebkitUserDrag: 'none' }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
              {
                plans.map(({ plan, label }) => (
                  <FormControlLabel key={plan} sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={id + '_' + plan} />} label={label} />
                ))
              }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Benefits</Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {
                  outpatientModules.map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      label={<Typography fontSize={13} sx={{ cursor: 'pointer' }}>{label}</Typography>}
                      control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={id + '_' + key} />} />
                  ))
                }
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={<Typography fontSize={13} sx={{ width: 55, cursor: 'pointer' }}>Dental</Typography>}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={id + '_dental'} />} />
                  <Select
                    size='small'
                    value={dentalPlan}
                    onChange={(e) => setDentalPlan(e.target.value)}
                    sx={{ fontSize: 12, height: 32, width: '90px' }}
                    disabled={loadingStatus[id]}
                  >
                    {
                      dentalPlans.map(({ key, label }) => (
                        <MenuItem key={key} value={key} sx={{ fontSize: 12 }}>{label}</MenuItem>
                      ))
                    }
                  </Select>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Deductible Option</Typography>
                <CustomSelect2
                  dense
                  value={deductibles[id]}
                  onChange={handleDeductibleChange}
                  items={deductibleOptions}
                  disabled={loadingStatus[id]}
                  sx={{ minWidth: 170 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id]}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    currencies.map(currency => (
                      <MenuItem key={currency} value={currency} sx={{ fontSize: 12 }}>{currency}</MenuItem>
                    ))
                  }
                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id]}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    paymentFrequencies.map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency} sx={{ fontSize: 12 }}>{payment_frequency}</MenuItem>
                    ))
                  }
                </Select>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts[id]}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={loadings[id]}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
            <CopyExcelDataButton
              elementId='table-hsbc-smart-care-optimum-enhanced-rates'
              style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
            <CopyImageButton
              elementId='table-hsbc-smart-care-optimum-enhanced-rates'
              setShowImageCopy={setShowImageCopy}
              style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table id='table-hsbc-smart-care-optimum-enhanced-rates' size='small' style={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#6d0015', color: '#fff' }}>
                  <TableCell style={{ width: 260, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div>  </TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  plans.map(({ plan, label }) => {
                    const planSelected = selectedPlans.includes(id + '_' + plan);

                    return (
                      <Fragment key={plan}>
                        {
                          planSelected &&
                          // Tier row carries the base plan premium
                          <TableRow style={{ backgroundColor: '#ffffff' }}>
                            <TableCell style={{ fontWeight: 600, background: '#b9f6ca', color: '#000' }}>{label}</TableCell>
                            {
                              clients.map((client, idx) => {
                                return <TableCell style={{ background: '#b9f6ca', color: '#000' }} key={idx}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(getBasePremiumPerClient(idx, plan))}</TableCell>
                              })
                            }
                            <TableCell style={{ background: '#b9f6ca', color: '#000' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeBasePremium(plan))}</TableCell>
                          </TableRow>
                        }

                        {
                          outpatientModules.map(({ key, label: moduleLabel }) => (
                            <TableRatesRow
                              key={key}
                              planName={plan + '_' + key}
                              planLabel={moduleLabel}
                              clients={clients}
                              selectedPlans={selectedPlans}
                              loading={loadingStatus[id]}
                              rates={rates}
                              currency={currency}
                              discount={discounts[id]}
                              additionalLoading={loadings[id]}
                              visible={planSelected && hasOutpatient(plan) && isModuleSelected(key)}
                            />
                          ))
                        }
                        <TableRatesRow
                          planName={plan + '_dental'}
                          planLabel={'Dental ' + getDentalPlanLabel()}
                          clients={clients}
                          selectedPlans={selectedPlans}
                          loading={loadingStatus[id]}
                          rates={rates}
                          currency={currency}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={planSelected && isModuleSelected('dental')}
                        />

                        {
                          planSelected &&
                          <TableRow style={{ backgroundColor: '#ffffff' }}>
                            <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#d5d7d8' }}>Total Premium</TableCell>
                            {
                              clients.map((client, idx) => {
                                return <TableCell style={{ border: '2px solid #fff', background: '#d5d7d8' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx, plan))}</TableCell>
                              })
                            }
                            <TableCell style={{ border: '2px solid #fff', background: '#d5d7d8' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremium(plan))}</TableCell>
                          </TableRow>
                        }

                        {
                          planSelected &&
                          <TableRow>
                            <TableCell colSpan={clients.length + 2} style={{ border: 'none', padding: 0, height: 24, background: '#fff' }} />
                          </TableRow>
                        }
                      </Fragment>
                    );
                  })
                }
              </TableBody>
            </Table>
          </TableContainer>

          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>
      </Paper>
    </Box>
  );
});

export default HSBC_SmartCareOptimumEnhanced_Rates;
